/**
 * API endpoints for Adventure Files/Media Management
 */

import { ApiService } from '../apiService'
import apiClient from '../client'
import {
  AdventureMediaFileDto,
  AdventureMediaFileDetailDto,
  AdventureMediaUploadResultDto,
  MediaFileUsageDto,
  AdventureFileSelectionResultDto,
  GetAdventureFilesParams,
  UpdateAdventureMediaFileRequest,
  SelectAdventureFileRequest,
  SetAdventureImageRequest,
  AdventureFileListResponse,
  AdventureFileUploadOptions,
} from '../types/adventureFiles'

export const adventureFilesApi = {
  /**
   * Get list of files for an adventure with filtering and pagination
   */
  getFiles: (adventureId: string, params?: GetAdventureFilesParams) =>
    ApiService.get<AdventureFileListResponse>(`/api/adventures/${adventureId}/files`, params),

  /**
   * Get detailed information about a specific file
   */
  getFile: (adventureId: string, fileId: string) =>
    ApiService.get<AdventureMediaFileDetailDto>(`/api/adventures/${adventureId}/files/${fileId}`),

  /**
   * Get usages of a file within the adventure
   */
  getFileUsages: (adventureId: string, fileId: string) =>
    ApiService.get<MediaFileUsageDto[]>(`/api/adventures/${adventureId}/files/${fileId}/usages`),

  /**
   * Upload a new file to the adventure
   */
  uploadFile: (adventureId: string, file: File, options?: AdventureFileUploadOptions) => {
    const formData = new FormData()
    formData.append('file', file)

    if (options?.displayName) {
      formData.append('displayName', options.displayName)
    }
    if (options?.description) {
      formData.append('description', options.description)
    }
    if (options?.tags && options.tags.length > 0) {
      options.tags.forEach((tag) => formData.append('tags', tag))
    }

    return ApiService.upload<AdventureMediaUploadResultDto>(
      `/api/adventures/${adventureId}/files/upload`,
      formData
    )
  },

  /**
   * Select/link an existing file to the adventure
   */
  selectFile: (adventureId: string, data: SelectAdventureFileRequest) =>
    ApiService.post<AdventureFileSelectionResultDto>(
      `/api/adventures/${adventureId}/files/select`,
      data
    ),

  /**
   * Update file metadata (display name, description, tags)
   */
  updateFile: (adventureId: string, fileId: string, data: UpdateAdventureMediaFileRequest) =>
    ApiService.patch<AdventureMediaFileDto>(
      `/api/adventures/${adventureId}/files/${fileId}`,
      data
    ),

  /**
   * Delete a file from the adventure
   */
  deleteFile: (adventureId: string, fileId: string) =>
    ApiService.delete<void>(`/api/adventures/${adventureId}/files/${fileId}`),

  /**
   * Download file content as blob (proxied through backend to avoid CORS)
   */
  downloadFile: async (adventureId: string, fileId: string): Promise<Blob> => {
    const response = await apiClient.get(`/api/adventures/${adventureId}/files/${fileId}/download`, {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Set or clear adventure thumbnail/cover image
   * @param adventureId - The adventure ID
   * @param mediaFileId - The file ID to set, or null to clear
   * @param usageContext - 'thumbnail' or 'cover'
   */
  setAdventureImage: (adventureId: string, mediaFileId: string | null, usageContext: 'thumbnail' | 'cover') =>
    ApiService.post<AdventureFileSelectionResultDto>(
      `/api/adventures/${adventureId}/files/select`,
      { mediaFileId, usageContext } as SetAdventureImageRequest
    ),
}
